/**
 * OOIA Heatpad Switcher
 *
 * When a customer selects "mit Heatpad" and clicks "In den Warenkorb", this script
 * intercepts the product-form submission, first adds the heatpad variant to the cart
 * via the /cart/add.js AJAX API, then re-submits the original form so the Prestige
 * theme's ProductForm component handles the main product normally (cart drawer, etc.).
 *
 * Intercepts the form's `submit` event in the capture phase so it always runs before
 * the ProductForm custom element's own listener.
 *
 * Required DOM:
 *   <fieldset class="ooia-heatpad-switcher"
 *             data-heatpad-variant-id="{variantId}"
 *             data-form-id="{formId}">
 *     ...
 *   </fieldset>
 */
(function () {
  'use strict';

  /**
   * Attach the heatpad interception logic to a single switcher container.
   * @param {Element} containerEl
   */
  function initSwitcher(containerEl) {
    const container = /** @type {HTMLElement} */ (containerEl);
    const formId = container.dataset.formId;
    const heatpadVariantId = parseInt(container.dataset.heatpadVariantId || '', 10);

    if (!formId || !heatpadVariantId) return;

    const form = /** @type {HTMLFormElement|null} */ (document.getElementById(formId));
    if (!(form instanceof HTMLFormElement)) return;

    // Toggle visual active state when radio inputs change
    container.addEventListener('change', () => updateVisualState(container));
    updateVisualState(container);

    // Flag to skip our interception on the re-submitted event
    let processingHeatpad = false;

    form.addEventListener(
      'submit',
      async function (event) {
        // Second pass after we call requestSubmit — let ProductForm handle it
        if (processingHeatpad) return;

        const selected = /** @type {HTMLInputElement|null} */ (container.querySelector('input[type="radio"]:checked'));
        if (!selected || selected.value !== 'with') return;

        // Intercept before the ProductForm custom element runs
        event.preventDefault();
        event.stopImmediatePropagation();

        try {
          /** @type {any} */
          const win = window;
          const cartRoot = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
          const res = await fetch(`${cartRoot}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: heatpadVariantId,
                  quantity: 1,
                  properties: { _pad: 'with Pad' },
                },
              ],
            }),
          });

          if (!res.ok) {
            console.warn(
              '[ooia-heatpad-switcher] Could not add heatpad to cart (status ' + res.status + '). Proceeding with main product.'
            );
          }
        } catch (err) {
          console.error('[ooia-heatpad-switcher] Network error while adding heatpad:', err);
          // Still attempt to add the main product below
        }

        // Re-submit the form — processingHeatpad=true bypasses our check so
        // the ProductForm component handles it normally (cart drawer, events, etc.)
        processingHeatpad = true;
        const submitter = /** @type {HTMLElement|null} */ (event.submitter || form.querySelector('[type="submit"]'));
        form.requestSubmit(submitter);
        processingHeatpad = false;
      },
      { capture: true }
    );
  }

  /**
   * Toggle .is-selected on the label/span matching the currently checked radio.
   * @param {HTMLElement} container
   */
  function updateVisualState(container) {
    container.querySelectorAll('.ooia-heatpad-switcher__option').forEach((optionEl) => {
      const option = /** @type {HTMLElement} */ (optionEl);
      const input = /** @type {HTMLInputElement|null} */ (option.querySelector('input[type="radio"]'));
      option.classList.toggle('is-selected', input ? input.checked : false);
    });
  }

  function init() {
    document
      .querySelectorAll('.ooia-heatpad-switcher[data-heatpad-variant-id]')
      .forEach(initSwitcher);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
